import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ConsultaForm from "./components/consultaform/ConsultaForm";
import ResultsGrid from "./components/resultsgrid/ResultsGrid";
import styles from "./ResultadosPage.module.css";
import { lotteryService } from "../../../services/api";
import {
  RESULTS_HISTORY_LIMIT,
  lotteriesFromResults,
  mapLotteryResultToGridItem,
} from "../../../services/resultsMapper";
import { searchLotteryResults } from "../../../services/resultsSearch";
import { Spinner, ErrorBanner, EmptyState } from "../../../components/ui";

const ResultadosPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialLotteryId = searchParams.get("lottery") || "";
  const initialDate = searchParams.get("date") || "";
  const [rawResults, setRawResults] = useState([]);
  const [lotteries, setLotteries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [consultMessage, setConsultMessage] = useState({ type: "", text: "" });
  const [filteredRaw, setFilteredRaw] = useState(null);
  const [searching, setSearching] = useState(false);
  const appliedUrl = useRef(false);
  const searchSeq = useRef(0);

  const loadResults = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [listRes, infoRes] = await Promise.all([
        lotteryService.listResults({ limit: RESULTS_HISTORY_LIMIT }),
        lotteryService.getLotteryInfo(),
      ]);

      if (listRes.ok && Array.isArray(listRes.data)) {
        setRawResults(listRes.data);
      } else {
        setRawResults([]);
        if (!listRes.ok) setError("No se pudieron cargar los resultados.");
      }

      if (infoRes.ok) {
        setLotteries(Array.isArray(infoRes.data) ? infoRes.data : []);
      }
    } catch (e) {
      console.error(e);
      setError("Error de conexión al cargar resultados.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadResults();
  }, [loadResults]);

  const searchLotteries = useMemo(
    () => lotteriesFromResults(rawResults, lotteries),
    [rawResults, lotteries]
  );

  /** Búsqueda filtrada en el backend (lotería + fecha) → encuentra sorteos antiguos. */
  const runSearch = useCallback(
    async (payload) => {
      const seq = ++searchSeq.current;
      setSearching(true);
      const result = await searchLotteryResults(payload, searchLotteries);
      if (seq !== searchSeq.current) return;
      setSearching(false);
      setFilteredRaw(result.matches);
      setConsultMessage(
        result.ok
          ? { type: "success", text: result.message }
          : { type: "error", text: result.error }
      );
    },
    [searchLotteries]
  );

  useEffect(() => {
    if (appliedUrl.current || loading) return;
    if (!initialLotteryId && !initialDate) return;
    appliedUrl.current = true;
    runSearch({ lotteryId: initialLotteryId, date: initialDate });
  }, [loading, runSearch, initialLotteryId, initialDate]);

  const source = filteredRaw ?? rawResults;
  const gridItems = useMemo(
    () => source.map((r, i) => mapLotteryResultToGridItem(r, i)),
    [source]
  );

  const handleConsultarNumero = (payload) => {
    runSearch(payload);
  };

  const handlePlay = (item) => {
    const lottery = lotteries.find(
      (l) => l.name === item.lotteryName || l.id === item.raw?.lottery_id
    );
    navigate("/apuestas", {
      state: lottery ? { selectedLottery: lottery } : undefined,
    });
  };

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {loading && <Spinner label="Cargando resultados…" />}
        {error && <ErrorBanner message={error} onRetry={loadResults} />}

        <ConsultaForm
          lotteries={searchLotteries}
          results={rawResults}
          initialLotteryId={initialLotteryId}
          initialDate={initialDate}
          onConsultarNumero={handleConsultarNumero}
          consultMessage={consultMessage}
          searching={searching}
        />

        {!loading && !error && gridItems.length === 0 && (
          <EmptyState
            title="Sin resultados"
            description={
              filteredRaw
                ? "No hay sorteos para esa consulta."
                : "Aún no hay sorteos publicados."
            }
          />
        )}
        {!loading && gridItems.length > 0 && (
          <ResultsGrid
            items={gridItems}
            onPlay={handlePlay}
            title={filteredRaw ? "Resultados de tu consulta" : "Últimos resultados"}
          />
        )}
      </div>
    </div>
  );
};

export default ResultadosPage;
